import { API_ORIGIN, getRequestPlatform, resolveApiPath } from '@/app/constants';
import {
  keeperListAvatarAssetScenes,
  keeperListAvatarAssets,
  keeperListEditingAssets,
  livesGetAvatar,
} from '@/app/services/live';
import { LiveClaimError, type LiveClaimErrorPayload, type LiveClaimRequest, type LiveClaimResponse } from '@/app/types';

const LIVE_CLAIM_PATH = resolveApiPath('/live/v1/live/claim');
const LIVE_CLAIM_REASON_NOT_ENOUGH_QUOTA = 'ClaimFailNotEnoughQuota';

export const getPublicAvatar = (avatarId: string, options: { accessToken?: string } = {}) =>
  livesGetAvatar({
    path: { avatar_id: avatarId },
    ...(options.accessToken ? { headers: { Authorization: `Bearer ${options.accessToken}` } } : {}),
  });

/** 获取当前用户可用的实时编辑内置素材。 */
export const listEditingAssets = (options: { accessToken?: string } = {}) =>
  keeperListEditingAssets({
    query: { 'pager.pagesz': 100 },
    ...(options.accessToken ? { headers: { Authorization: `Bearer ${options.accessToken}` } } : {}),
  });

export const listAvatarAssetScenes = (options: { accessToken?: string } = {}) =>
  keeperListAvatarAssetScenes({
    query: { 'pager.pagesz': 100 },
    ...(options.accessToken ? { headers: { Authorization: `Bearer ${options.accessToken}` } } : {}),
  });

export const listAvatarAssets = (avatarId: string, options: { accessToken?: string } = {}) =>
  keeperListAvatarAssets({
    query: { avatar_id: avatarId, 'pager.pagesz': 100 },
    ...(options.accessToken ? { headers: { Authorization: `Bearer ${options.accessToken}` } } : {}),
  });

const parseLiveClaimErrorPayload = async (response: Response): Promise<LiveClaimErrorPayload | null> => {
  try {
    return (await response.json()) as LiveClaimErrorPayload;
  } catch {
    return null;
  }
};

const hasRequiredLiveClaimFields = (data: LiveClaimResponse): boolean => {
  const liveId = data.live?.id?.trim();
  const channelId = data.rtc?.channel_id?.trim();
  const token = data.rtc?.token?.trim();
  const userId = data.rtc?.user_id?.trim();

  return Boolean(liveId && channelId && token && userId);
};

export const claimLive = async (
  body: LiveClaimRequest,
  options: { accessToken?: string } = {},
): Promise<LiveClaimResponse> => {
  const response = await fetch(`${API_ORIGIN}${LIVE_CLAIM_PATH}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Platform': getRequestPlatform(),
      ...(options.accessToken ? { Authorization: `Bearer ${options.accessToken}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorPayload = await parseLiveClaimErrorPayload(response);
    const isUnauthorized = response.status === 401 || errorPayload?.reason === 'Unauthorized';

    throw new LiveClaimError({
      code: isUnauthorized
        ? 'unauthorized'
        : errorPayload?.reason === LIVE_CLAIM_REASON_NOT_ENOUGH_QUOTA
          ? 'claim_not_enough_quota'
          : 'request_failed',
      message: errorPayload?.message?.trim() || `Live claim failed with status ${response.status}`,
      reason: errorPayload?.reason,
      status: response.status,
    });
  }

  const data = (await response.json()) as LiveClaimResponse;

  if (!data.is_success) {
    throw new LiveClaimError({
      code: 'request_failed',
      message: 'Live claim was not successful',
    });
  }

  if (!hasRequiredLiveClaimFields(data)) {
    throw new LiveClaimError({
      code: 'invalid_claim_response',
      message: 'Live claim response is missing required fields',
    });
  }

  return data;
};
